/**
 * Document Source Provider Utilities
 *
 * Centralized utilities for document source provider icons, colors, and status indicators.
 * Eliminates code duplication across document source configuration and connection pages.
 *
 * @module documentSourceHelpers
 * @author SmartDoc AI Team
 * @date 2025-10-28
 */

import React from 'react';
import {
  Cloud as CloudIcon,
  CloudQueue as OneDriveIcon,
  CloudCircle as DropboxIcon,
  CloudOutlined as NextcloudIcon,
  Folder as FolderIcon,
} from '@mui/icons-material';

/**
 * Provider icon mapping for document source providers.
 * Maps provider type strings to MUI icons.
 *
 * Supported Providers:
 * - onedrive: Microsoft OneDrive
 * - dropbox: Dropbox
 * - nextcloud: Nextcloud
 * - sharepoint: Microsoft SharePoint (uses OneDrive icon)
 * - default: Generic cloud icon for unknown providers
 *
 * @constant
 */
export const PROVIDER_ICONS = {
  onedrive: OneDriveIcon,
  dropbox: DropboxIcon,
  nextcloud: NextcloudIcon,
  sharepoint: OneDriveIcon, // SharePoint uses same icon as OneDrive
  docuware: CloudIcon, // DocuWare default
  default: CloudIcon,
} as const;

/**
 * Provider color mapping for document source providers.
 * Maps provider type strings to brand colors for visual consistency.
 *
 * Color Scheme:
 * - onedrive: #0078D4 (Microsoft Blue)
 * - dropbox: #0061FE (Dropbox Blue)
 * - nextcloud: #0082C9 (Nextcloud Blue)
 * - sharepoint: #0078D4 (Microsoft Blue)
 * - default: #666 (Neutral Gray)
 *
 * @constant
 */
export const PROVIDER_COLORS = {
  onedrive: '#0078D4', // Microsoft blue
  dropbox: '#0061FE', // Dropbox blue
  nextcloud: '#0082C9', // Nextcloud blue
  sharepoint: '#0078D4', // Microsoft blue
  docuware: '#092d6b', // DocuWare Navy Blue
  default: '#666', // Neutral gray
} as const;

/**
 * Status color mapping for connection and sync status indicators.
 * Maps status strings to MUI Chip color variants.
 *
 * Status Colors:
 * - active: success (green) - Connection is active and working
 * - inactive: default (gray) - Connection is inactive/paused
 * - error: error (red) - Connection has errors
 * - pending: warning (yellow) - Connection is pending setup
 * - syncing: info (blue) - Currently syncing
 * - default: default (gray) - Unknown status
 *
 * @constant
 */
export const STATUS_COLORS = {
  active: 'success',
  inactive: 'default',
  error: 'error',
  pending: 'warning',
  syncing: 'info',
  default: 'default',
} as const;

/**
 * Get provider icon component for a given provider type.
 * Returns the appropriate Material-UI icon component as a React element.
 *
 * @param type - Provider type string (e.g., 'onedrive', 'dropbox', 'nextcloud')
 * @returns React element containing the provider icon
 *
 * @example
 * ```tsx
 * // Get OneDrive icon
 * const icon = getProviderIcon('onedrive');
 * return <ListItemIcon>{icon}</ListItemIcon>;
 * ```
 *
 * @example
 * ```tsx
 * // Get icon for unknown provider (returns default cloud icon)
 * const icon = getProviderIcon('unknown-provider');
 * ```
 */
export const getProviderIcon = (type: string): React.ReactElement => {
  const IconComponent = PROVIDER_ICONS[type as keyof typeof PROVIDER_ICONS] || PROVIDER_ICONS.default;
  return <IconComponent />;
};

/**
 * Get provider brand color for a given provider type.
 * Returns hex color string for styling provider-related UI elements.
 *
 * @param type - Provider type string (e.g., 'onedrive', 'dropbox', 'nextcloud')
 * @returns Hex color string (e.g., '#0078D4')
 *
 * @example
 * ```tsx
 * // Use provider color for avatar background
 * const color = getProviderColor('dropbox');
 * return <Avatar sx={{ bgcolor: color }}>D</Avatar>;
 * ```
 *
 * @example
 * ```tsx
 * // Apply provider color to text
 * const color = getProviderColor('onedrive');
 * return <Typography sx={{ color }}>OneDrive</Typography>;
 * ```
 */
export const getProviderColor = (type: string): string =>
  PROVIDER_COLORS[type as keyof typeof PROVIDER_COLORS] || PROVIDER_COLORS.default;

/**
 * Get status color variant for Material-UI Chip component.
 * Returns MUI Chip color prop value for status indicators.
 *
 * @param status - Status string (e.g., 'active', 'error', 'pending', 'syncing')
 * @returns MUI Chip color variant ('success' | 'error' | 'warning' | 'info' | 'default')
 *
 * @example
 * ```tsx
 * // Display connection status
 * const color = getStatusColor('active');
 * return <Chip label="Active" color={color} size="small" />;
 * ```
 *
 * @example
 * ```tsx
 * // Display error status
 * const color = getStatusColor('error');
 * return <Chip label="Error" color={color} size="small" />;
 * ```
 */
export const getStatusColor = (status: string): 'success' | 'error' | 'warning' | 'info' | 'default' =>
  STATUS_COLORS[status as keyof typeof STATUS_COLORS] || STATUS_COLORS.default;

/**
 * Get folder icon for file tree rendering.
 * Simple alias for FolderIcon to maintain consistency.
 *
 * @returns React element containing folder icon
 *
 * @example
 * ```tsx
 * // Use in file tree
 * const icon = getFolderIcon();
 * return <TreeItemIcon>{icon}</TreeItemIcon>;
 * ```
 */
export const getFolderIcon = (): React.ReactElement => <FolderIcon />;

/**
 * Format provider type name for display.
 * Capitalizes provider name and handles special cases.
 *
 * @param type - Provider type string (e.g., 'onedrive', 'dropbox')
 * @returns Formatted provider name (e.g., 'OneDrive', 'Dropbox')
 *
 * @example
 * ```tsx
 * // Format for display
 * const name = formatProviderName('onedrive'); // Returns "OneDrive"
 * return <Typography>{name}</Typography>;
 * ```
 *
 * @example
 * ```tsx
 * // Handle special cases
 * const name = formatProviderName('sharepoint'); // Returns "SharePoint"
 * ```
 */
export const formatProviderName = (type: string): string => {
  const specialCases: Record<string, string> = {
    onedrive: 'OneDrive',
    dropbox: 'Dropbox',
    nextcloud: 'Nextcloud',
    sharepoint: 'SharePoint',
    docuware: 'DocuWare',
  };

  return specialCases[type.toLowerCase()] || type.charAt(0).toUpperCase() + type.slice(1);
};

/**
 * Check if provider type is supported.
 * Validates if a provider type has associated icon and color mappings.
 *
 * @param type - Provider type string to validate
 * @returns True if provider is supported, false otherwise
 *
 * @example
 * ```tsx
 * // Check before rendering
 * if (isProviderSupported('onedrive')) {
 *   return <ProviderCard type="onedrive" />;
 * }
 * ```
 */
export const isProviderSupported = (type: string): boolean => {
  return type in PROVIDER_ICONS && type !== 'default';
};

/**
 * Get provider metadata bundle.
 * Returns all provider-related information in a single object for convenience.
 *
 * @param type - Provider type string
 * @returns Object containing icon, color, and formatted name
 *
 * @example
 * ```tsx
 * // Get all provider info at once
 * const { icon, color, name } = getProviderMetadata('dropbox');
 * return (
 *   <Card>
 *     <Avatar sx={{ bgcolor: color }}>{icon}</Avatar>
 *     <Typography>{name}</Typography>
 *   </Card>
 * );
 * ```
 */
export const getProviderMetadata = (type: string) => ({
  icon: getProviderIcon(type),
  color: getProviderColor(type),
  name: formatProviderName(type),
  isSupported: isProviderSupported(type),
});
